import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ElementType,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { ParrotLogo, ParrotLogomark } from '@parrot/icons';
import { cx } from '@parrot/utils';
import { useAnchoredPosition } from '../internal/anchor';
import styles from './Sidebar.module.css';

/*
 * The rail's timings. These have to agree with the width transition in the stylesheet,
 * which is why the numbers live together rather than being spread across both files.
 *
 * Expansion is driven from here rather than from :hover, because a flyout may only open
 * once the rail has stopped moving — and "hovered long enough to have finished widening"
 * is not something CSS can say.
 */
const EXPAND_DELAY = 120;
/** Matches --duration-base. */
const WIDTH_DURATION = 200;
const SETTLED = EXPAND_DELAY + WIDTH_DURATION;
/** Long enough for an open flyout to have gone before the rail moves out from under it. */
const COLLAPSE_DELAY = 100;

/* Links inside a flyout are not rows of the rail — the glider tracks the group header
   they belong to, not the item in the panel. */
const InFlyoutContext = createContext(false);

/* Whether the rail is wide and has stopped moving. A group asks before opening: a flyout
   that appears mid-slide gets dragged sideways as the rail widens under it. */
const SettledContext = createContext(false);

const showPopover = (element: HTMLElement | null) => {
  if (element === null || typeof element.showPopover !== 'function') return;
  if (!element.matches(':popover-open')) element.showPopover();
};

const hidePopover = (element: HTMLElement | null) => {
  if (element === null || typeof element.hidePopover !== 'function') return;
  if (element.matches(':popover-open')) element.hidePopover();
};

type Logo = {
  /** Shown on the collapsed rail, fitted to a square. An `<img>` or an `<svg>`. */
  mark: ReactNode;
  /** Shown once the rail widens, at a fixed height and its own width. Omit to keep the mark. */
  full?: ReactNode;
};

/* Themed by the logos themselves, so there's nothing to swap per theme here. */
const PARROT_LOGO: Logo = { mark: <ParrotLogomark />, full: <ParrotLogo /> };

type SidebarProps<T extends ElementType> = Omit<HTMLAttributes<HTMLElement>, 'children'> & {
  /** Held open regardless of hover. The caller owns it, and owns persisting it. */
  pinned: boolean;
  onPinnedChange: (pinned: boolean) => void;
  /** Sits above the nav, and links home. Defaults to the Parrot logo. */
  logo?: Logo;
  /**
   * The element the logo renders as. Defaults to `a`; pass your router's link so going
   * home stays client-side — `logoAs={Link} logoProps={{ to: '/' }}`.
   */
  logoAs?: T;
  /** Props for `logoAs`. Defaults to `{ href: '/' }` while `logoAs` is left as `a`. */
  logoProps?: Omit<ComponentPropsWithoutRef<T>, 'children' | 'className'>;
  /** Sits below it — a theme toggle, a version string. */
  footer?: ReactNode;
  /** SidebarLink, SidebarGroup and SidebarDivider, in the order they should appear. */
  children: ReactNode;
};

/**
 * The application rail. A column of icons that widens to show labels on hover, and stays
 * wide when pinned.
 *
 * Placement is the host's: this owns its width and how it expands, but not where it sits.
 * Give it `position: fixed` (or absolute) through `className` — expanded in normal flow
 * it pushes the page sideways on hover, which is not the intent.
 *
 * Nothing here knows about routing. `SidebarLink` and the logo render whatever element you
 * give them, and the active item is read from `aria-current="page"` — which react-router's
 * `NavLink` sets for free, and a plain anchor can set by hand.
 *
 * The logo owns a slot, not a size: a fixed height, with the width following whatever is
 * passed, so a logo of any aspect ratio lands the same way in every app.
 */
export const Sidebar = <T extends ElementType = 'a'>({
  pinned,
  onPinnedChange,
  logo = PARROT_LOGO,
  logoAs,
  logoProps,
  footer,
  children,
  className,
  ...navProps
}: SidebarProps<T>) => {
  const LogoLink = logoAs ?? 'a';
  // href only while it's still a plain anchor: a router link takes `to`, not `href`.
  const linkProps = logoProps ?? (logoAs === undefined ? { href: '/' } : {});

  const navRef = useRef<HTMLDivElement>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  /* Two steps, not one. `wide` drives the width; `settled` says the width has arrived and
     flyouts may open. Pinning is both at once — it's a deliberate act, so it doesn't wait. */
  const [wide, setWide] = useState(false);
  const [settled, setSettled] = useState(false);

  const clearTimers = useCallback(() => {
    for (const timer of timers.current) clearTimeout(timer);
    timers.current = [];
  }, []);

  const enter = useCallback(() => {
    clearTimers();
    timers.current.push(
      setTimeout(() => setWide(true), EXPAND_DELAY),
      setTimeout(() => setSettled(true), SETTLED),
    );
  }, [clearTimers]);

  const leave = useCallback(() => {
    clearTimers();
    /* Flyouts are barred first and the rail collapses a beat later, so an open panel is
       gone before the thing it was anchored to starts moving. */
    setSettled(false);
    timers.current.push(setTimeout(() => setWide(false), COLLAPSE_DELAY));
  }, [clearTimers]);

  useEffect(() => clearTimers, [clearTimers]);

  const isWide = wide || pinned;
  const isSettled = settled || pinned;

  /* The glider — the bar that slides to whatever is current. It reads the DOM rather than
     taking a prop because the active item is decided by the router inside SidebarLink,
     and `aria-current` is the one thing every router agrees to publish. */
  useEffect(() => {
    const nav = navRef.current;
    if (nav === null) return;

    const update = () => {
      const current = nav.querySelector('[aria-current="page"]');
      const row = current?.closest('[data-nav-row]');

      if (!(row instanceof HTMLElement)) {
        nav.style.setProperty('--glider-shown', '0');
        return;
      }

      /* The row, not the item. A link inside a flyout resolves to the group header it
         hangs off, which is what keeps the marker on screen while its panel is shut. */
      nav.style.setProperty('--glider-y', `${row.offsetTop}px`);
      nav.style.setProperty('--glider-h', `${row.offsetHeight}px`);
      nav.style.setProperty('--glider-shown', '1');
    };

    update();

    /* Two observers, because the two things that move the marker look nothing alike:
       navigating swaps the attribute without touching layout, and anything that changes
       the list's size moves the rows without touching the attribute. */
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : undefined;
    resize?.observe(nav);

    const mutation =
      typeof MutationObserver === 'function' ? new MutationObserver(update) : undefined;
    mutation?.observe(nav, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-current'],
    });

    return () => {
      resize?.disconnect();
      mutation?.disconnect();
    };
  }, []);

  return (
    <nav
      className={cx(styles.base, className)}
      data-pinned={pinned ? '' : undefined}
      data-expanded={isWide ? '' : undefined}
      data-settled={isSettled ? '' : undefined}
      onPointerEnter={enter}
      onPointerLeave={leave}
      aria-label={'Main'}
      {...navProps}
    >
      <div className={styles.head}>
        <LogoLink className={styles.logo} {...linkProps}>
          {/* With no full logo, the mark stays through the widening rather than leaving
              the head empty. */}
          <span className={cx(styles.mark, logo.full !== undefined && styles.markCollapsedOnly)}>
            {logo.mark}
          </span>
          {logo.full !== undefined && <span className={styles.full}>{logo.full}</span>}
        </LogoLink>
        <button
          type={'button'}
          className={styles.pin}
          onClick={() => onPinnedChange(!pinned)}
          aria-pressed={pinned}
          aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
        >
          <PanelIcon />
        </button>
      </div>

      <div ref={navRef} className={styles.nav}>
        <span className={styles.glider} aria-hidden={'true'} />
        <SettledContext.Provider value={isSettled}>{children}</SettledContext.Provider>
      </div>

      {footer !== undefined && <div className={styles.footer}>{footer}</div>}
    </nav>
  );
};

type LinkOwnProps = {
  /** Sized in em against the row, so an unsized SVG still lands right. */
  icon?: ReactNode;
  /** After the label — a "new" pill, a count. Hidden while collapsed, like the label. */
  trailing?: ReactNode;
  children: ReactNode;
};

type SidebarLinkProps<T extends ElementType> = LinkOwnProps & {
  /**
   * The element to render. Defaults to `a`; pass your router's link so navigation stays
   * client-side — `as={NavLink} to={'/reports'}`.
   */
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, keyof LinkOwnProps | 'as'>;

export const SidebarLink = <T extends ElementType = 'a'>({
  as,
  icon,
  trailing,
  children,
  className,
  ...rest
}: SidebarLinkProps<T>) => {
  const Component = as ?? 'a';
  const inFlyout = useContext(InFlyoutContext);

  return (
    <Component
      // Only rail items are rows. Inside a flyout the group header is the row.
      data-nav-row={inFlyout ? undefined : ''}
      className={cx(styles.link, className)}
      {...rest}
    >
      <span className={styles.icon} aria-hidden={'true'}>
        {icon}
      </span>
      <span className={styles.label}>{children}</span>
      {trailing !== undefined && <span className={styles.trailing}>{trailing}</span>}
    </Component>
  );
};

type SidebarGroupProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
  icon?: ReactNode;
  label: ReactNode;
  /** SidebarLinks. They only ever appear in the flyout — never inline, in either state. */
  children: ReactNode;
};

/**
 * A rail entry whose children live in a panel that opens to its right, in both the
 * collapsed and expanded states.
 *
 * The panel is a popover, so it draws in the top layer and can't be clipped — but it
 * stays a descendant in the DOM, which is what makes `pointerleave` cover the panel as
 * well as the header. Moving the pointer across into it doesn't close it, with no timers
 * and no portal-event bookkeeping.
 */
export const SidebarGroup = ({
  icon,
  label,
  children,
  className,
  ...divProps
}: SidebarGroupProps) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const settled = useContext(SettledContext);

  useAnchoredPosition({
    elementRef: flyoutRef,
    getAnchor: () => rowRef.current,
    side: 'right',
    align: 'start',
    gap: 0,
  });

  /* Driven from both conditions rather than straight off the pointer, so that hovering a
     group before the rail has finished widening still opens its panel — the moment the
     rail settles, not on a second pass of the mouse. */
  useEffect(() => {
    if (hovered && settled) showPopover(flyoutRef.current);
    else hidePopover(flyoutRef.current);
  }, [hovered, settled]);

  return (
    <div
      ref={rowRef}
      data-nav-row={''}
      className={cx(styles.group, className)}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      {...divProps}
    >
      <div className={styles.link}>
        <span className={styles.icon} aria-hidden={'true'}>
          {icon}
        </span>
        <span className={styles.label}>{label}</span>
        <ChevronRightIcon />
      </div>

      <div ref={flyoutRef} popover={'manual'} className={styles.flyout}>
        <InFlyoutContext.Provider value={true}>{children}</InFlyoutContext.Provider>
      </div>
    </div>
  );
};

/** Separates clusters of items. The only grouping the design uses — there are no headings. */
export const SidebarDivider = ({ className, ...props }: HTMLAttributes<HTMLHRElement>) => (
  <hr className={cx(styles.divider, className)} {...props} />
);

const ChevronRightIcon = () => (
  <svg
    className={styles.chevron}
    viewBox={'0 0 24 24'}
    fill={'none'}
    stroke={'currentColor'}
    strokeWidth={2}
    strokeLinecap={'round'}
    strokeLinejoin={'round'}
    aria-hidden={'true'}
  >
    <path d={'m9 18 6-6-6-6'} />
  </svg>
);

/*
 * The panel mark, not a drawing pin. It's what @parrot/parrot-chat already uses for the
 * same job — the affordance on its collapsed rail that opens the sidebar — so the two
 * rails read as the same control rather than two inventions.
 *
 * Inlined because libs/ui carries no icon dependency. A candidate to lift into
 * @parrot/icons once that library holds more than a placeholder.
 */
const PanelIcon = () => (
  <svg
    viewBox={'0 0 24 24'}
    fill={'none'}
    stroke={'currentColor'}
    strokeWidth={2}
    strokeLinecap={'round'}
    strokeLinejoin={'round'}
    aria-hidden={'true'}
  >
    <rect x={'3'} y={'4'} width={'18'} height={'16'} rx={'2'} />
    <path d={'M9 4v16'} />
  </svg>
);
